import React, { useEffect, useRef, useCallback, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ReactFlow,
  Controls,
  MiniMap,
  Background,
  addEdge,
  useNodesState,
  useEdgesState,
  ControlButton,
  useReactFlow,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import AddNodeButton from "../components/flow/AddNodeButton";
import useSelectedNode from "../hooks/flowhooks/useSelectedNode";
import useAutomations from "../hooks/automationhooks/useAutomations";
import { Button, Tooltip } from "@mui/material";
import ROUTES from "../routerfolder/Routes";
import AddNodeContext from "../contexts/AddNodeContext";
import ActionEdge from "../components/flow/edges/ActionEdge";
import useIdGenerator from "../hooks/flowhooks/useIdGenerator";
import useNodeManager from "../hooks/flowhooks/useNodeManager";
import useSave from "../hooks/flowhooks/useSave";
import getNodeTypes from "../utils/workflow/getNodeTypes";
import ConditionEdge from "../components/flow/edges/ConditionEdge";
import { LightMode, DarkMode } from "@mui/icons-material";
import NodeSideMenu from "../components/flow/menus/NodeSideMenu";
import { useThemeContext } from "../Theme/Theme";
import GoBackButton from "../components/GoBackButton";

const HELP_ICON_BG =
  "url(\"data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M12 8.33333H12.0122M10.7778 12H12V16.8889H13.2222M1 12C1 13.4445 1.28452 14.8749 1.83733 16.2095C2.39013 17.5441 3.20038 18.7567 4.22183 19.7782C5.24327 20.7996 6.4559 21.6099 7.79048 22.1627C9.12506 22.7155 10.5555 23 12 23C13.4445 23 14.8749 22.7155 16.2095 22.1627C17.5441 21.6099 18.7567 20.7996 19.7782 19.7782C20.7996 18.7567 21.6099 17.5441 22.1627 16.2095C22.7155 14.8749 23 13.4445 23 12C23 9.08262 21.8411 6.28473 19.7782 4.22183C17.7153 2.15893 14.9174 1 12 1C9.08262 1 6.28473 2.15893 4.22183 4.22183C2.15893 6.28473 1 9.08262 1 12Z' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")";

export default function Workflow() {
  const { automationId, brandId } = useParams();
  const { handleGetFlow, handleEditJourneyAutomation, isLoading } =
    useAutomations();
  const { onNodeClick, onPaneClick, selectedNode } = useSelectedNode();
  const { mode, toggleTheme } = useThemeContext();
  // Side Menu State
  const [sideMenuOpen, setSideMenuOpen] = useState(false);
  const [nodeFormData, setNodeFormData] = useState({});

  const [tempAutomation, setTempAutomation] = useState(null);
  const [rfInstance, setRfInstance] = useState(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [colorMode, setColorMode] = useState(mode);
  const generateId = useIdGenerator();
  const {
    addNodeBetweenNodes,
    addNode,
    onNodesDelete,
    editNode,
    cleanupOrphanedExitNodes,
  } = useNodeManager(nodes, edges, setNodes, setEdges, generateId);
  const navigate = useNavigate();
  const nodeTypes = getNodeTypes();
  const initialized = useRef(false);
  const edgeTypes = {
    actionEdge: ActionEdge,
    conditionEdge: ConditionEdge,
  };

  const onSave = useSave(
    rfInstance,
    tempAutomation,
    automationId,
    handleEditJourneyAutomation
  );

  useEffect(() => {
    if (selectedNode) {
      // Populate form data with node info
      setNodeFormData({
        label: selectedNode.data?.label || "",
        description: selectedNode.data?.description || "",
      });
      setSideMenuOpen(true);
    } else {
      setSideMenuOpen(false);
    }
  }, [selectedNode]);

  useEffect(() => {
    const loadAutomation = async () => {
      try {
        const targetAutomation = await handleGetFlow(automationId);
        if (!targetAutomation) {
          console.error("Automation not found");
          navigate(ROUTES.ROOT);
          return;
        }

        setTempAutomation(targetAutomation);

        if (targetAutomation && !initialized.current) {
          setNodes(targetAutomation.nodes || []);
          setEdges(targetAutomation.edges || []);
          initialized.current = true;
        }
      } catch (error) {
        console.error("Failed to load automation:", error);
      }
    };

    loadAutomation();
  }, []);

  const onEdgesDelete = useCallback(
    (deletedEdges) => {
      // Collect all new nodes and edges to add in one batch
      const newExitNodes = [];
      const newExitEdges = [];

      deletedEdges.forEach((deletedEdge) => {
        const sourceNode = nodes.find((node) => node.id === deletedEdge.source);

        // Check if source is a condition or custom node
        const isConditionNode =
          sourceNode.type === "conditionNode" ||
          sourceNode.type === "customNodeCommand";

        // For condition/custom nodes: check if THIS HANDLE has other edges
        // For regular nodes: check if the node has any other edges
        let sourceHandleHasOtherEdges;

        if (isConditionNode && deletedEdge.sourceHandle) {
          // Check if this specific handle has other edges
          sourceHandleHasOtherEdges = edges.some(
            (edge) =>
              edge.source === deletedEdge.source &&
              edge.sourceHandle === deletedEdge.sourceHandle &&
              edge.id !== deletedEdge.id
          );
        } else {
          // Regular node - check if any other edges exist
          sourceHandleHasOtherEdges = edges.some(
            (edge) =>
              edge.source === deletedEdge.source && edge.id !== deletedEdge.id
          );
        }

        // Only create exit node if this handle has NO other outgoing edges
        if (!sourceHandleHasOtherEdges) {
          // Check if there's already another exit node connected to this source with same handle
          const existingExitEdge = edges.find(
            (edge) =>
              edge.source === deletedEdge.source &&
              edge.target.includes("exitNode") &&
              edge.sourceHandle === deletedEdge.sourceHandle &&
              edge.id !== deletedEdge.id
          );

          // Only create exit node if one doesn't already exist for this source handle
          if (!existingExitEdge) {
            // Determine position based on source handle for condition/custom nodes
            const isLeftHandle =
              deletedEdge.sourceHandle === "yes" ||
              deletedEdge.sourceHandle === "match";
            const isRightHandle =
              deletedEdge.sourceHandle === "no" ||
              deletedEdge.sourceHandle === "nomatch";

            let exitNodeX, exitNodeY;

            if (isLeftHandle) {
              exitNodeX = sourceNode.position.x - 100;
              exitNodeY = sourceNode.position.y + 100;
            } else if (isRightHandle) {
              exitNodeX =
                sourceNode.position.x +
                (sourceNode.measured?.width || 200) +
                50;
              exitNodeY = sourceNode.position.y + 100;
            } else {
              exitNodeX =
                sourceNode.position.x +
                (sourceNode.measured?.width || 200) / 2 -
                20;
              exitNodeY = sourceNode.position.y + 150;
            }

            // Create a new exit node
            const exitNodeId = generateId("exitNode", nodes);
            const newExitNode = {
              id: exitNodeId,
              type: "exitNode",
              data: { label: "Exit" },
              deletable: false,
              position: {
                x: exitNodeX,
                y: exitNodeY,
              },
            };

            // Create new edge from source to exit node
            const newEdge = {
              id: generateId("edge", edges),
              source: deletedEdge.source,
              target: exitNodeId,
              type: deletedEdge.type,
              sourceHandle: deletedEdge.sourceHandle,
              data: deletedEdge.data || {},
            };
            newExitNodes.push(newExitNode);
            newExitEdges.push(newEdge);
          }
        }
      });

      // Update edges: remove deleted ones and add new exit edges in one operation
      setEdges((currentEdges) => {
        const filtered = currentEdges.filter(
          (edge) =>
            !deletedEdges.some((deletedEdge) => deletedEdge.id === edge.id)
        );
        return [...filtered, ...newExitEdges];
      });

      // Add new exit nodes
      if (newExitNodes.length > 0) {
        setNodes((currentNodes) => {
          return [...currentNodes, ...newExitNodes];
        });
      }
    },
    [nodes, edges, setNodes, setEdges, generateId, cleanupOrphanedExitNodes]
  );

  const onConnect = useCallback(
    (params) => {
      const { sourceHandle, source, target } = params;

      // Get target node to check if it's an exit node
      const targetNode = nodes.find((node) => node.id === target);

      // Prevent connection to exit nodes
      if (targetNode && targetNode.type === "exitNode") {
        return; // Block the connection
      }

      const sourceNode = nodes.find((node) => node.id === source);
      const isConditionNode = sourceNode?.type === "conditionNode";
      const isCustomNodeCommand = sourceNode?.type === "customNodeCommand";

      // Find existing edge to EXIT NODE with the same sourceHandle
      // We only want to remove edges going to exit nodes
      const existingExitEdge = edges.find((edge) => {
        if (edge.source !== source) return false;

        // Check if target is an exit node
        const edgeTargetNode = nodes.find((n) => n.id === edge.target);
        if (!edgeTargetNode || edgeTargetNode.type !== "exitNode") return false;

        // For condition/custom nodes, match the sourceHandle
        if (sourceHandle) {
          return edge.sourceHandle === sourceHandle;
        }

        // For regular nodes, any exit edge from this source
        return true;
      });

      // If there's an existing edge to exit node, remove it
      if (existingExitEdge) {
        const oldExitNodeId = existingExitEdge.target;

        // Remove the edge to exit node
        setEdges((eds) => eds.filter((e) => e.id !== existingExitEdge.id));

        // Check if this exit node has any other incoming edges
        const otherIncomingEdges = edges.filter(
          (edge) =>
            edge.target === oldExitNodeId && edge.id !== existingExitEdge.id
        );

        // If no other edges point to it, delete the exit node
        if (otherIncomingEdges.length === 0) {
          setNodes((nds) => nds.filter((n) => n.id !== oldExitNodeId));
        }
      }

      // Generate a unique edge ID
      const newEdgeId = generateId("edge", edges);

      // Create the new edge data with explicit ID
      let edgeData = {
        ...params,
        id: newEdgeId,
        type:
          isConditionNode || isCustomNodeCommand
            ? "conditionEdge"
            : "actionEdge",
        data: {},
      };

      // Add label for condition/custom nodes
      if (isConditionNode || isCustomNodeCommand) {
        let label = "";
        if (isConditionNode) {
          if (sourceHandle === "yes") {
            label = "Yes";
          } else if (sourceHandle === "no") {
            label = "No";
          }
        } else if (isCustomNodeCommand) {
          if (sourceHandle === "match") {
            label = "Match";
          } else if (sourceHandle === "nomatch") {
            label = "No Match";
          }
        }
        edgeData.data.label = label;
      }

      // Add the new edge
      setEdges((eds) => addEdge(edgeData, eds));
    },
    [setEdges, nodes, edges, setNodes, generateId]
  );

  const toggleColorMode = useCallback(() => {
    setColorMode((prevMode) => (prevMode === "dark" ? "light" : "dark"));
    toggleTheme();
  }, []);

  if (isLoading || !tempAutomation) {
    return <div>Loading...</div>;
  }

  const handleSideMenuSave = (formData) => {
    console.log(formData);

    if (selectedNode) {
      editNode(selectedNode.id, formData);
      formData = {};
    }
    setSideMenuOpen(false);
  };

  const handleSideMenuCancel = () => {
    setNodeFormData({});
    setSideMenuOpen(false);
  };

  return (
    <AddNodeContext.Provider
      value={{
        addNodeBetweenNodes,
        addNode,
        editNode,
        onNodesDelete,
        onEdgesDelete,
        setSelectedNode: (node) => {
          const foundNode = nodes.find((n) => n.id === node.id);
          if (foundNode) {
            onNodeClick(null, foundNode);
          }
        },
      }}
    >
      <div style={{ width: "100vw", height: "100vh", position: "relative" }}>
        <Button
          style={{
            position: "absolute",
            top: 28,
            textTransform: "none",
            right: 20,
            zIndex: 1,
          }}
          onClick={onSave}
          variant="contained"
          sx={{
            backgroundColor: "#0DA3E2",
            borderRadius: "7px",
            width: "143px",
            height: "50px",
            minWidth: "143px",
            px: "10px",
            py: "10px",
            gap: "10px",
            textTransform: "none",
            fontWeight: 700,
            color: "#fff",
            "&:hover": {
              backgroundColor: "#0DA3E2",
              opacity: 0.92,
            },
          }}
        >
          Save
        </Button>
        <GoBackButton
          style={{
            position: "absolute",
            top: 28,
            left: 20,
            zIndex: 1,
          }}
          onClick={() =>
            (window.location.href = `https://icreate-campaign.com/admin/Campaigns/Index?app=1004&brand=${brandId}`)
          }
        />

        <div
          style={{
            position: "absolute",
            top: 104,
            left: 23,
            zIndex: 1,
          }}
        >
          <Tooltip
            title={
              <>
                Click and drag nodes to move them.
                <br />
                Click and drag the background to pan.
                <br />
                Click and drag while holding shift to select multiple nodes.
                <br />
                Press control while scrolling to zoom in and out.
                <br />
                Press backspace while a node is selected to delete it.
              </>
            }
          >
            <Button
              variant="contained"
              style={{
                width: 58,
                height: 58,
                minWidth: 58,
                borderRadius: 100,
                padding: 10,
              }}
              sx={{ backgroundColor: "#0DA3E2" }}
            >
              <span
                style={{
                  width: 24,
                  height: 24,
                  backgroundImage: HELP_ICON_BG,
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "center",
                  backgroundSize: "contain",
                  display: "inline-block",
                }}
              />
            </Button>
          </Tooltip>
        </div>

        <AddNodeButton nodesExist={nodes.length > 0} />

        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onEdgesDelete={onEdgesDelete}
          onNodesDelete={onNodesDelete}
          colorMode={colorMode}
          onConnect={onConnect}
          onInit={setRfInstance}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          onNodeClick={onNodeClick}
          onPaneClick={onPaneClick}
          panOnScroll={true}
          fitView
          deleteKeyCode={null}
        >
          <Controls orientation="horizontal">
            <ControlButton
              onClick={toggleColorMode}
              title={`Switch to ${
                colorMode === "dark" ? "light" : "dark"
              } mode`}
            >
              {colorMode === "dark" ? <LightMode /> : <DarkMode />}
            </ControlButton>
          </Controls>
          <MiniMap />
          <Background variant="dots" gap={12} size={1} />
        </ReactFlow>

        {/* Use the extracted NodeSideMenu component */}
        <NodeSideMenu
          open={sideMenuOpen}
          selectedNode={selectedNode}
          onSave={handleSideMenuSave}
          onCancel={handleSideMenuCancel}
          setMenu={setSideMenuOpen}
        />
      </div>
    </AddNodeContext.Provider>
  );
}
